// react/utils
import {
  addMinutes,
  differenceInMinutes,
  endOfDay,
  getHours,
  getMinutes,
  isSameDay,
  set,
  startOfDay
} from 'date-fns';
import { useState } from 'react';
import { toMinute } from '../PeriodInput.utils';

// types
import type { Period } from '../PeriodInput.types';

type UsePeriodInputStateArgs = {
  period: Period;
  sameDayOnly?: boolean;
};

/**
 * Owns what the four fields hold and everything derivable from it: the composed start, the end
 * the duration lands on, and the bounds the end-time picker gets. Pure -- no parent callbacks
 * and no effects. The choreography that decides when a change is worth reporting lives in
 * `usePeriodInputSyncReporting`.
 *
 * The timestamps returned here are what that hook's effects key on, and they are numbers on
 * purpose: the `Date` props and the dates derived from them are a fresh object identity every
 * render, so an effect depending on those would re-run every render the moment this file
 * stopped being compiled -- and an annotation-mode bailout is silent. Numbers rule that out by
 * construction, and comparing them needs no date library at all.
 */
export const usePeriodInputState = ({
  period,
  sameDayOnly
}: UsePeriodInputStateArgs) => {
  'use memo';

  const { start, end } = period;

  const [startDate, setStartDate] = useState<Date>();
  const [startTime, setStartTime] = useState<Date>();
  const [durationHours, setDurationHours] = useState(String(0));
  const [durationMinutes, setDurationMinutes] = useState(String(0));

  const totalMinutes = Number(durationHours) * 60 + Number(durationMinutes);

  const normalizedStartDate = startDate && toMinute(startDate);
  const normalizedStartTime = startTime && toMinute(startTime);

  const fullStart =
    normalizedStartDate && normalizedStartTime
      ? set(normalizedStartDate, {
          hours: getHours(normalizedStartTime),
          minutes: getMinutes(normalizedStartTime)
        })
      : undefined;

  const calculatedEnd = fullStart && addMinutes(fullStart, totalMinutes);
  const fullEnd =
    !fullStart || !calculatedEnd
      ? undefined
      : sameDayOnly && !isSameDay(fullStart, calculatedEnd)
      ? endOfDay(fullStart)
      : calculatedEnd;

  const propsStartMs = toMinute(start).getTime();
  const propsEndMs = toMinute(end).getTime();
  // Minute-clean by construction: `fullStart` is built from truncated values.
  const fullStartMs = fullStart ? fullStart.getTime() : null;
  const fullEndMs = fullEnd ? fullEnd.getTime() : null;
  // A `sameDayOnly` end is an end-of-day value, which carries seconds the props never will.
  // The exact `fullEndMs` is what gets reported; this is what it gets compared on.
  const fullEndMinuteMs = fullEnd ? toMinute(fullEnd).getTime() : null;

  const sameDay = !!fullStart && !!fullEnd && isSameDay(fullStart, fullEnd);
  const minEndTime: Date | null =
    !fullStart || !fullEnd ? null : sameDayOnly || sameDay ? fullStart : null;
  const maxEndTime: Date | null =
    !fullStart || !fullEnd
      ? null
      : sameDayOnly
      ? endOfDay(fullStart)
      : sameDay
      ? null
      : fullStart;

  /**
   * Writes an incoming period to all four fields at once, so the sync effect calling this does
   * not have to know which fields there are. Takes timestamps rather than the `Date` props for
   * the reason above.
   */
  const syncFromProps = (startMs: number, endMs: number) => {
    const periodStart = new Date(startMs);

    setStartDate(startOfDay(periodStart));
    setStartTime(periodStart);

    const diff = Math.max(differenceInMinutes(new Date(endMs), periodStart), 0);

    setDurationHours(String(Math.floor(diff / 60)));
    setDurationMinutes(String(diff % 60));
  };

  return {
    startDate,
    startTime,
    durationHours,
    durationMinutes,
    setStartDate,
    setStartTime,
    setDurationHours,
    setDurationMinutes,
    syncFromProps,
    fullStart,
    fullEnd,
    minEndTime,
    maxEndTime,
    propsStartMs,
    propsEndMs,
    fullStartMs,
    fullEndMs,
    fullEndMinuteMs
  };
};
